import React, { createContext, useEffect } from 'react';
import {
  useAccount,
  useConnect,
  usePublicClient,
  useWalletClient,
  useDisconnect,
  useSwitchChain,
  useReconnect,
} from 'wagmi';
import { useStateIfMounted } from 'use-state-if-mounted';
import { createClient } from '@reservoir0x/reservoir-sdk';

import {
  DEFAULT_NETWORK,
  RESERVOIR_CHAINS,
  RESERVOIR_CHAINS_LIST,
  RESERVOIR_GMI_DOMAIN,
} from 'constants/index';

export const WalletContext = createContext(null);

export const WalletProvider = ({ children }) => {
  const { address: rawAddress, isConnected, chainId } = useAccount();
  const { connect, connectors: rawConnectors, connectAsync } = useConnect();
  const { disconnect } = useDisconnect();
  const { data: signer } = useWalletClient({ chainId });
  const provider = usePublicClient();
  const { switchChainAsync } = useSwitchChain();
  const { reconnect } = useReconnect();

  const [walletModalOpen, setWalletModalOpen] = useStateIfMounted(false);

  // Check URL for chain parameter
  const getInitialChain = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const chainFromUrl = urlParams.get('chain') || urlParams.get('chainId');
    if (chainFromUrl) {
      return Number(chainFromUrl);
    }
    return Number(localStorage.getItem('storedChain')) || DEFAULT_NETWORK;
  };

  const [storedChain, setStoredChain] = useStateIfMounted(getInitialChain());
  const [wrongNetwork, setWrongNetwork] = useStateIfMounted(false);

  const address = rawAddress?.toLowerCase();
  const isInstalled = typeof window.ethereum !== 'undefined';
  const connectors = rawConnectors
    .slice()
    .sort((prev, next) => (prev.name === 'MetaMask' ? -1 : next.name === 'MetaMask' ? 1 : 0));

  const globalChain = isConnected ? chainId : storedChain;

  const updateStoredChain = (targetChain) => {
    localStorage.setItem('storedChain', targetChain);
    setStoredChain(Number(targetChain));
  };

  const switchToNetwork = async (chainId) => {
    return await switchChainAsync({ chainId });
  };

  // Prevent auto-reconnection if user manually disconnected
  useEffect(() => {
    const manuallyDisconnected = localStorage.getItem('manuallyDisconnected');
    if (manuallyDisconnected === 'true' && isConnected) {
      // User previously disconnected manually but wagmi auto-reconnected
      // Disconnect again to respect user's choice
      disconnect();
    }
  }, [isConnected, disconnect]);

  useEffect(() => {
    if (RESERVOIR_CHAINS_LIST.includes(chainId)) {
      createClient({
        chains: [
          {
            ...RESERVOIR_CHAINS[chainId],
            active: true,
          },
        ],
        source: RESERVOIR_GMI_DOMAIN,
        apiKey: process.env.REACT_APP_RESERVOIR_API_KEY,
      });
    }
  }, [chainId]);

  useEffect(() => {
    if (!localStorage.getItem('storedChain')) {
      localStorage.setItem('storedChain', DEFAULT_NETWORK);
    }
  }, []);

  // Enhanced disconnect function that tracks manual disconnection
  const enhancedDisconnect = () => {
    localStorage.setItem('manuallyDisconnected', 'true');
    disconnect();
  };

  // Enhanced connect function that clears manual disconnection flag
  const enhancedConnect = (connectorOrOptions) => {
    localStorage.removeItem('manuallyDisconnected');
    return connect(connectorOrOptions);
  };

  const enhancedConnectAsync = async (connectorOrOptions) => {
    localStorage.removeItem('manuallyDisconnected');
    const result = await connectAsync(connectorOrOptions);

    // After successful connection, if wallet is on wrong chain, try to switch
    if (result && result.accounts && result.accounts.length > 0) {
      const currentAppChain = storedChain;
      if (result.chainId !== currentAppChain) {
        try {
          await switchToNetwork(currentAppChain);
        } catch (err) {
          console.warn('Failed to switch to app chain after connection:', err);
          // User rejected the switch or chain not added to wallet
          // The app will still work but show content for the wallet's chain
        }
      }
    }

    return result;
  };

  // Handle chain from URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const chainFromUrl = urlParams.get('chain') || urlParams.get('chainId');

    if (chainFromUrl) {
      const targetChain = Number(chainFromUrl);

      // Update stored chain to match URL parameter
      if (storedChain !== targetChain) {
        updateStoredChain(targetChain);
      }

      // If connected and on wrong chain, try to switch
      if (isConnected && chainId !== targetChain) {
        switchToNetwork(targetChain).catch((err) => {
          console.error('Failed to switch network:', err);
        });
      }

      // Clean up URL after processing
      urlParams.delete('chain');
      const newUrl =
        window.location.pathname + (urlParams.toString() ? '?' + urlParams.toString() : '');
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  return (
    <WalletContext.Provider
      value={{
        isInstalled,
        isConnected,
        connectors,
        connect: enhancedConnect,
        connectAsync: enhancedConnectAsync,
        provider,
        signer,
        chainId,
        address,
        disconnect: enhancedDisconnect,
        switchToNetwork,
        walletModalOpen,
        setWalletModalOpen,
        wrongNetwork,
        setWrongNetwork,
        storedChain,
        globalChain,
        updateStoredChain,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
};
